import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

/**
 * Der Benutzer trägt Rolle und Dienst-Berechtigungen:
 * {username, role, role_label, permissions: ['suche', 'ki_suche', ...], full_name}
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const cached = localStorage.getItem('user');

    if (!token) {
      setLoading(false);
      return;
    }

    // Sofort aus dem Cache rendern, dann autoritativ vom Server bestätigen
    // (Rollenänderungen durch Admins wirken so beim nächsten Laden).
    if (cached) {
      try {
        setUser(JSON.parse(cached));
      } catch (e) {
        localStorage.removeItem('user');
      }
    }

    authAPI
      .getCurrentUser()
      .then((response) => {
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
      })
      .catch(() => {
        // 401 wird vom API-Interceptor behandelt; hier nur Zustand räumen
        localStorage.removeItem('access_token');
        localStorage.removeItem('username');
        localStorage.removeItem('user');
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (username, password) => {
    try {
      const response = await authAPI.login(username, password);
      const { access_token, ...userData } = response.data;

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('username', userData.username);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Anmeldung fehlgeschlagen',
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('username');
    localStorage.removeItem('user');
    setUser(null);
    authAPI.logout().catch(() => {});
  };

  /** Prüft, ob der angemeldete Benutzer eine Dienst-Berechtigung besitzt. */
  const hasPermission = (permission) =>
    Boolean(user?.permissions?.includes(permission));

  const isAdmin = hasPermission('benutzerverwaltung');

  return (
    <AuthContext.Provider
      value={{ user, login, logout, loading, hasPermission, isAdmin }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
